// Circuit de validation (CV-3) : ce que l'application montre d'une instance, sans rien décider elle-même.
// Règles (docs/taches/2026-10-22-circuit-de-validation.md, §§ 3.8 à 3.10, 4.7, 4.9) :
// - une décision se prend dans Ekera, par un geste sur la clé de sécurité, jamais depuis un lien ni un paramètre d'URL ;
// - la décision fait partie du défi : un geste demandé pour « refuser » ne vaut pas « approuver » ;
// - refuser et demander une modification exigent un motif ;
// - le silence ne vaut jamais accord : aucune décision n'est présélectionnée.

import type { ContenuSoumisVue } from '../api/types';

export type DecisionEtape = 'approuve' | 'refuse' | 'modification_demandee' | 'avis';
export type ValeurAvis = 'favorable' | 'reserve' | 'defavorable';

export interface EtapeVue {
  id: string;
  rang: number;
  libelle: string;
  tenue_par?: string;
  nature: 'approbation' | 'avis' | string;
  quorum?: number | string;
  raisons?: string[];
  bloquee?: boolean;
  motif_blocage?: string | null;
  terminee?: boolean;
  ouverte?: boolean;
}

export interface VoixVue {
  etape: string;
  decision: string;
  avis?: string | null;
  le: string;
  de_l_auteur?: boolean;
  tombee?: boolean;
  motif_tombee?: string | null;
  par_delegation?: boolean;
  /** Sans rôle d'accès, ``sub`` et ``emetteur`` des autres personnes sont masqués (CV3I-4). */
  personne?: { sub: string | null; emetteur?: string | null; nom?: string | null };
}

export interface InstanceVue {
  id: string;
  type_objet: string;
  objet?: string;
  carte?: string | null;
  etat: string;
  empreinte_objet: string;
  reference: string;
  ouverte_le?: string;
  echeance?: string | null;
  auteur_admis?: boolean;
  rang_ouvert?: number | null;
  etapes: EtapeVue[];
  voix?: VoixVue[];
  montant?: { valeur?: number | null; calculable?: boolean; composantes?: unknown[]; [k: string]: unknown } | null;
  motif?: string | null;
  recalcul?: { caduque?: boolean; motifs?: string[]; ecart_montant?: number | null; montant_fin?: number | null } | null;
  precedente?: string | null;
  corrections_par_membres?: { element: string; sub?: string; le?: string }[];
  objet_detail?: Record<string, unknown>;
  attestation?: string | null;
  /** Publication : la version exacte soumise, en lecture seule (CV3I-1). */
  contenu_soumis?: ContenuSoumisVue | null;
  /** Publication : faux si le livrable a changé depuis la soumission. */
  contenu_actuel?: boolean | null;
}

export interface ADecider {
  instance: string;
  reference: string;
  type_objet: string;
  etape: string;
  libelle: string;
  par: string;
  auteur: boolean;
  ouverte_le: string;
  echeance?: string | null;
}

export interface MoiCircuit {
  roles_circuit: string[];
  configuration: number | null;
  suspendu: boolean;
  absent: boolean;
  delegations_donnees: { delegation: string; role: string; fin: string; terminee_le?: string | null }[];
  delegations_recues: { delegation: string; role: string; fin: string }[];
  /** État des clés de la personne (comme /v0/moi), pour l'enrôlement depuis l'espace du circuit. */
  webauthn?: Record<string, unknown> | null;
}

export interface DefiCircuit {
  defi_id: string;
  options: Record<string, unknown>;
  expire: string;
}

export const LIBELLE_TYPE: Record<string, string> = {
  publication_livrable: 'Publication d’un document',
  ecriture_outil: 'Écriture dans un outil',
  decision_variante: 'Décision sur un scénario de travaux',
  configuration_circuit: 'Configuration du circuit',
};

export const LIBELLE_ETAT: Record<string, string> = {
  en_cours: 'En cours',
  approuvee: 'Approuvée, à exécuter',
  refusee: 'Refusée',
  a_modifier: 'Modification demandée',
  caduque: 'Caduque',
  retiree: 'Retirée',
  expiree: 'Expirée',
  executee: 'Exécutée',
};

export const LIBELLE_ROLE: Record<string, string> = {
  dsi: 'DSI',
  rssi: 'RSSI',
  daf: 'Directeur financier',
  achats: 'Achats',
  direction: 'Direction',
  architecte: 'Architecte',
  metier: 'Métier',
  dpo: 'Délégué à la protection des données',
  administration_circuit: 'Administration du circuit',
};

export const LIBELLE_DECISION: Record<string, string> = {
  approuve: 'Approuvé',
  refuse: 'Refusé',
  modification_demandee: 'Modification demandée',
  avis: 'Avis',
};

export const LIBELLE_AVIS: Record<string, string> = { favorable: 'favorable', reserve: 'réservé', defavorable: 'défavorable' };

export const LIBELLE_ENVOI: Record<string, string> = {
  en_attente: 'Courriel pas encore envoyé',
  envoye: 'Courriel envoyé',
  echec: 'Courriel en échec (nouvel essai prévu)',
};

/** Un motif est obligatoire pour refuser ou demander une modification (§ 3.10). */
export function motifRequis(d: DecisionEtape): boolean {
  return d === 'refuse' || d === 'modification_demandee';
}

/** Les étapes où la personne peut se prononcer maintenant sur cette instance (boîte « À décider »). */
export function etapesPourMoi(liste: ADecider[], iid: string): ADecider[] {
  return liste.filter((x) => x.instance === iid);
}

/** La délégation par laquelle la personne se prononce (« delegation:<id> »), ou null. */
export function delegationDe(par: string): string | null {
  return par.startsWith('delegation:') ? par.slice('delegation:'.length) : null;
}

/** Identifiant d'instance attendu dans un lien `#/circuits/<id>` (32 caractères hexadécimaux), sinon null. */
export function instanceDuLien(segment: string | undefined): string | null {
  return segment && /^[0-9a-f]{32}$/.test(segment) ? segment : null;
}

/** Les étapes terminées, en cours et à venir, dans l'ordre des rangs. */
export function etapesOrdonnees(etapes: EtapeVue[]): EtapeVue[] {
  return [...etapes].sort((a, b) => a.rang - b.rang || a.id.localeCompare(b.id));
}

/** Les avis défavorables ou réservés sont montrés en tête (§ 3.3). */
export function avisEnTete(voix: VoixVue[]): VoixVue[] {
  const poids = (v: VoixVue) => (v.avis === 'defavorable' ? 0 : v.avis === 'reserve' ? 1 : 2);
  return [...voix].sort((a, b) => poids(a) - poids(b) || a.le.localeCompare(b.le));
}

/** Un fichier JSON à télécharger (attestation) : rien d'autre que le contenu rendu par l'API. */
export function fichierAttestation(iid: string, att: unknown): { nom: string; contenu: string } {
  return { nom: `attestation-${iid.slice(0, 8)}.json`, contenu: `${JSON.stringify(att, null, 1)}\n` };
}
